/**
 * The alert rule form (New / Edit alert rule, both apps): plain questions, a sentence on top that
 * says what the rule will do, and at the bottom the rule EXACTLY as it will be saved — with Edit
 * for the few things the questions do not cover.
 *
 *   1  Name                          first, so everything below reads as "this rule"
 *   2  Which event?                  main event → sub event (or type your own); the names the rule
 *                                    will match are filled in and editable (eventAliases.ts)
 *   3  Which cameras?                all · a group · one camera · some cameras (camera_ids)
 *   4  How sure must the AI be?
 *   5  How serious is it?
 *   6  Don't repeat it for…
 *   7  Where should it go?           the alert board (live) and/or Routing notifications — which
 *                                    of them list this rule (saved after the rule: ruleRouting.ts)
 *   8  Alert message                 a suggestion for the event, variables to click in, a preview
 *   The exact rule                   every stored value, read-only; Edit opens extra conditions,
 *                                    hold time, fallback text and the webhook
 *
 * Who gets email / WhatsApp is NOT the rule's — it is Routing.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { LinkIcon, PencilSquareIcon, PlusIcon, XMarkIcon, MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';
import { aaInputCls, aaLabelCls } from '../alertActions/AlertActionsControls';
import { isDashboardOn, type PolicyDirectory, type RoutingPoliciesApi, type RoutingPolicy } from '../alertActions/alertActionsModel';
import { notificationsOfRule, onlyRuleOf, whoSummary } from '../alertActions/ruleRouting';
import { parseEventTypeTokens, type EventTypeOption } from './EventTypeFields';
import RuleConditionsFields from './RuleConditionsFields';
import { conditionsSummary, fieldsFor, BUILTIN_FIELDS, type EventCatalogEntry } from './ruleConditionsModel';
import {
  GENERIC_TEMPLATE, MESSAGE_VARIABLES, eventChoices, inferChoice, previewMessage, spellings, uniqueNames,
  type EventChoice, type EventFamily,
} from './eventAliases';

export interface AlertRuleFormProps {
  form: Record<string, any>;
  setForm: (next: Record<string, any>) => void;
  cameras: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  catalog: EventCatalogEntry[];
  /** The app's built-in detection list — kept for callers; the form uses eventAliases. */
  builtin?: EventTypeOption[];
  /** Where "who gets email / WhatsApp" is set — the Routing tab. */
  routingHref?: string;
  /**
   * Routing for this rule: the site's notifications API and the rule's id (null for a new rule).
   * The ticked notifications go in `form.__routing` — the page applies them after saving the rule
   * (applyRuleRouting) and strips the key before the rule is sent.
   */
  routing?: { api: RoutingPoliciesApi; ruleId: string | null };
}

const COOLDOWNS = [
  { value: 0, label: 'Every time it is seen' },
  { value: 30, label: '30 seconds' },
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 3600, label: '1 hour' },
];

const SEVERITIES = [
  { value: '*', label: 'Let the system decide', hint: 'Fire, falls and intrusions are critical; the rest are warnings' },
  { value: 'critical', label: 'Critical', hint: 'Needs action now' },
  { value: 'warning', label: 'Warning', hint: 'Someone should look' },
  { value: 'normal', label: 'For information', hint: 'Nothing to do' },
];
const SEV_DOT: Record<string, string> = { critical: 'bg-destructive', warning: 'bg-warning', normal: 'bg-info', '*': 'bg-muted-foreground' };
const CUSTOM = '__custom__';

function sureText(pct: number) {
  if (pct < 40) return 'Very sensitive — catches more, but expect false alarms';
  if (pct < 70) return 'Balanced — the usual choice';
  return 'Strict — only when the AI is sure, may miss some';
}

function Step({ n, title, hint, children }: { n: number; title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="flex gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-[12px] font-semibold text-white">{n}</span>
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <h4 className="text-[14px] font-semibold text-foreground">{title}</h4>
          {hint && <p className="text-[12px] text-muted-foreground">{hint}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}

function Choice({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`rounded-lg border px-3 py-1.5 text-[13px] transition-colors ${on ? 'border-brand bg-brand-light font-medium text-brand' : 'border-border text-foreground hover:bg-muted'}`}
    >
      {children}
    </button>
  );
}

/** Searchable checkboxes — "some cameras". */
function CameraPick({ cameras, selected, onChange }: { cameras: { id: string; name: string }[]; selected: string[]; onChange: (ids: string[]) => void }) {
  const [q, setQ] = useState('');
  const shown = cameras.filter((c) => !q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="max-w-md rounded-lg border border-border">
      <div className="flex items-center gap-1.5 border-b border-border px-2 py-1">
        <MagnifyingGlassIcon className="h-3.5 w-3.5 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a camera" className="w-full bg-transparent py-0.5 text-[13px] text-foreground outline-none placeholder:text-muted-foreground" />
        <span className="shrink-0 text-[11px] text-muted-foreground">{selected.length} picked</span>
      </div>
      <div className="max-h-44 overflow-y-auto py-1">
        {shown.length === 0 && <p className="px-2.5 py-1.5 text-[12px] text-muted-foreground">No cameras.</p>}
        {shown.map((c) => (
          <label key={c.id} className="flex cursor-pointer items-center gap-2 px-2.5 py-1 text-[13px] text-foreground hover:bg-muted">
            <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="accent-brand" />
            {c.name}
          </label>
        ))}
      </div>
    </div>
  );
}

/** The names the rule matches — chips with ✕, and a box to add one (with its other spellings). */
function NameChips({ names, onChange }: { names: string[]; onChange: (next: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    if (!draft.trim()) return;
    onChange(uniqueNames([...names, ...spellings(draft)]));
    setDraft('');
  };
  return (
    <div className="rounded-lg border border-border p-2">
      <div className="flex flex-wrap gap-1.5">
        {names.length === 0 && <span className="px-1 text-[12px] text-muted-foreground">Any event — nothing to match by name.</span>}
        {names.map((n) => (
          <span key={n} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted py-0.5 pl-2.5 pr-1 font-mono text-[12px] text-foreground">
            {n}
            <button type="button" onClick={() => onChange(names.filter((x) => x !== n))} className="rounded-full p-0.5 hover:bg-background" aria-label={`Remove ${n}`}>
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex max-w-md gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add another name, e.g. fire_catch"
          className={`${aaInputCls} mt-0 font-mono`}
        />
        <button type="button" onClick={add} className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-border px-2.5 text-[12px] text-foreground hover:bg-muted">
          <PlusIcon className="h-3.5 w-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

function Row({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 border-b border-border py-1.5 last:border-0">
      <dt className="text-[12px] text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-words text-[13px] text-foreground">{children}</dd>
    </div>
  );
}

export default function AlertRuleForm({ form, setForm, cameras, groups, catalog, routingHref, routing }: AlertRuleFormProps) {
  const set = (patch: Record<string, any>) => setForm({ ...form, ...patch });

  // Routing: the AI notifications, and which of them list this rule.
  const [notes, setNotes] = useState<{ list: RoutingPolicy[]; dir: PolicyDirectory | null; error: string } | null>(null);
  useEffect(() => {
    if (!routing) return;
    let gone = false;
    Promise.all([routing.api.list('ai'), routing.api.directory().catch(() => null)])
      .then(([list, dir]) => { if (!gone) setNotes({ list, dir, error: '' }); })
      .catch((err) => { if (!gone) setNotes({ list: [], dir: null, error: err?.response?.data?.message || err?.message || 'Could not read the notifications' }); });
    return () => { gone = true; };
  }, [routing?.api, routing?.ruleId]); // eslint-disable-line react-hooks/exhaustive-deps
  const custom = (notes?.list || []).filter((p) => !p.is_default);
  const fallback = (notes?.list || []).find((p) => p.is_default);
  const picked: string[] = Array.isArray(form.__routing) ? form.__routing : notificationsOfRule(custom, routing?.ruleId);
  const togglePick = (id: string) => set({ __routing: picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id] });
  const names = parseEventTypeTokens(form.event_type).filter((t) => t !== '*');
  const setNames = (next: string[]) => set({ event_type: next.length ? uniqueNames(next).join(',') : '*' });

  const families = useMemo(() => eventChoices(catalog || []), [catalog]);
  // Which main / sub event the pickers show: what the user picked here, else what the rule's
  // names point at. CUSTOM = typed by hand.
  const inferred = useMemo(() => inferChoice(names, families), [names.join(','), families]); // eslint-disable-line react-hooks/exhaustive-deps
  const [mainSel, setMainSel] = useState<string | null>(null);
  const [subSel, setSubSel] = useState<string | null>(null);
  const [mainCustom, setMainCustom] = useState('');
  const [subCustom, setSubCustom] = useState('');
  const main = mainSel ?? (inferred.family?.value || (names.length ? CUSTOM : ''));
  const family: EventFamily | null = families.find((f) => f.value === main) || null;
  const sub = subSel ?? (inferred.sub?.value || '');
  const subChoice: EventChoice | null = family?.subs.find((s) => s.value === sub) || null;

  const pickMain = (v: string) => {
    setMainSel(v);
    setSubSel('');
    const f = families.find((x) => x.value === v);
    const patch: Record<string, any> = {};
    if (!v) patch.event_type = '*';
    else if (f) patch.event_type = uniqueNames(f.aliases).join(',');
    if (f && !String(form.name || '').trim()) patch.name = f.label;
    if (f && !String(form.message_template || '').trim()) patch.message_template = f.template;
    set(patch);
  };
  const pickSub = (v: string) => {
    setSubSel(v);
    const s = family?.subs.find((x) => x.value === v);
    // The name follows the pick while it still is the family's own name (not one the user typed).
    const renamable = !String(form.name || '').trim() || form.name === family?.label || family?.subs.some((x) => x.label === form.name);
    if (s) set({ event_type: uniqueNames(s.aliases).join(','), ...(renamable ? { name: s.label } : {}) });
    else if (!v && family) set({ event_type: uniqueNames(family.aliases).join(','), ...(renamable ? { name: family.label } : {}) });
  };
  const applyCustom = (mainName: string, subName: string) => {
    const picked = subName.trim() ? spellings(subName) : spellings(mainName);
    if (picked.length) setNames(picked);
  };

  // Cameras
  const scope = Array.isArray(form.camera_ids) ? 'cameras' : form.camera_id ? 'camera' : form.group_id ? 'group' : 'all';
  const setScope = (s: string) => set({
    camera_id: s === 'camera' ? form.camera_id || cameras[0]?.id || '' : null,
    group_id: s === 'group' ? form.group_id || groups[0]?.id || '' : null,
    camera_ids: s === 'cameras' ? (Array.isArray(form.camera_ids) ? form.camera_ids : []) : null,
  });
  const camName = (id: string) => cameras.find((c) => c.id === id)?.name || id;
  const groupName = groups.find((g) => g.id === form.group_id)?.name;
  const listed: string[] = Array.isArray(form.camera_ids) ? form.camera_ids : [];

  const pct = Math.round((parseFloat(form.min_confidence) || 0) * 100);
  const cooldown = Number(form.cooldown_seconds) || 0;
  const severity = form.severity || '*';

  // Message
  const msgRef = useRef<HTMLTextAreaElement | null>(null);
  const eventFields = useMemo(
    () => Object.keys(fieldsFor(catalog || [], names)).filter((f) => !(f in BUILTIN_FIELDS)).slice(0, 12),
    [catalog, names.join(',')], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const insertVar = (v: string) => {
    const el = msgRef.current;
    const text = String(form.message_template || '');
    const token = `\${${v}}`;
    const at = el ? el.selectionStart ?? text.length : text.length;
    const end = el ? el.selectionEnd ?? at : at;
    set({ message_template: `${text.slice(0, at)}${token}${text.slice(end)}`.slice(0, 500) });
    requestAnimationFrame(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = at + token.length; } });
  };
  const suggested = family?.template || GENERIC_TEMPLATE;

  const [editExact, setEditExact] = useState(false);

  // The sentence
  const whereText = scope === 'camera' ? camName(form.camera_id) || 'one camera'
    : scope === 'cameras' ? (listed.length ? (listed.length <= 3 ? listed.map(camName).join(', ') : `${listed.length} chosen cameras`) : 'the cameras you pick')
      : scope === 'group' ? (groupName ? `a camera in ${groupName}` : 'a camera in a group') : 'any camera';
  const whatText = subChoice?.label || family?.label || (names.length ? names.slice(0, 2).join(' or ') : 'anything the AI detects');
  const sevText = severity === '*' ? 'an alert' : severity === 'normal' ? 'an information alert' : `a ${severity} alert`;
  const cooldownText = cooldown ? ` At most one every ${COOLDOWNS.find((c) => c.value === cooldown)?.label || `${cooldown} seconds`} per camera.` : '';
  const conditions = conditionsSummary(form);

  return (
    <div className="space-y-5 p-5">
      <p className="rounded-xl border border-border bg-muted px-3 py-2.5 text-[13px] leading-relaxed text-foreground">
        <b>{form.name || 'This rule'}</b>: raise <b>{sevText}</b> when <b>{whatText}</b> is seen on <b>{whereText}</b>,
        if the AI is at least <b>{pct}%</b> sure.{cooldownText}
        {!isDashboardOn(form) && <> It will <b>not</b> show on the alert board.</>}
      </p>

      <Step n={1} title="Name of the rule" hint="Shown on every alert this rule raises.">
        <input value={form.name || ''} onChange={(e) => set({ name: e.target.value })} className={`${aaInputCls} max-w-md`} placeholder="e.g. Fire in the warehouse" />
      </Step>

      <Step n={2} title="Which event?" hint="Pick the main event, then — if you want only one kind of it — the sub event. Or type your own.">
        <div className="grid max-w-2xl grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block">
            <span className={aaLabelCls}>Main event</span>
            <select value={main} onChange={(e) => pickMain(e.target.value)} className={aaInputCls}>
              <option value="">Any event</option>
              {families.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              <option value={CUSTOM}>Type my own…</option>
            </select>
          </label>
          <label className="block">
            <span className={aaLabelCls}>Sub event</span>
            <select value={main === CUSTOM ? CUSTOM : sub} onChange={(e) => pickSub(e.target.value)} disabled={!family} className={aaInputCls}>
              <option value="">{family ? `Any ${family.label.toLowerCase()} event` : '—'}</option>
              {(family?.subs || []).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              {main === CUSTOM && <option value={CUSTOM}>Typed below</option>}
            </select>
          </label>
          {main === CUSTOM && (
            <>
              <input value={mainCustom} onChange={(e) => setMainCustom(e.target.value)} onBlur={() => applyCustom(mainCustom, subCustom)}
                placeholder="Event, e.g. fire_smoke" className={`${aaInputCls} mt-0 font-mono`} />
              <input value={subCustom} onChange={(e) => setSubCustom(e.target.value)} onBlur={() => applyCustom(mainCustom, subCustom)}
                placeholder="Sub event (optional), e.g. fire_detect" className={`${aaInputCls} mt-0 font-mono`} />
            </>
          )}
        </div>
        <div className="max-w-2xl">
          <p className={aaLabelCls}>This rule matches events sent under any of these names — remove or add as you need</p>
          <NameChips names={names} onChange={setNames} />
          <p className="mt-1 text-[11px] text-muted-foreground">Capitals, spaces, “-” and “_” don't matter: “Fire-Detect” and “fire detect” are the same name.</p>
        </div>
      </Step>

      <Step n={3} title="Which cameras?">
        <div className="flex flex-wrap gap-1.5">
          <Choice on={scope === 'all'} onClick={() => setScope('all')}>All cameras</Choice>
          <Choice on={scope === 'group'} onClick={() => setScope('group')}>A group of cameras</Choice>
          <Choice on={scope === 'camera'} onClick={() => setScope('camera')}>One camera</Choice>
          <Choice on={scope === 'cameras'} onClick={() => setScope('cameras')}>Some cameras</Choice>
        </div>
        {scope === 'group' && (
          <select value={form.group_id || ''} onChange={(e) => set({ group_id: e.target.value })} className={`${aaInputCls} max-w-md`}>
            <option value="" disabled>Pick a group…</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
        {scope === 'camera' && (
          <select value={form.camera_id || ''} onChange={(e) => set({ camera_id: e.target.value })} className={`${aaInputCls} max-w-md`}>
            <option value="" disabled>Pick a camera…</option>
            {cameras.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        {scope === 'cameras' && <CameraPick cameras={cameras} selected={listed} onChange={(ids) => set({ camera_ids: ids })} />}
        {scope !== 'all' && <p className="text-[11px] text-muted-foreground">If another rule covers the same event for more cameras, this one wins on these.</p>}
      </Step>

      <Step n={4} title="How sure must the AI be?">
        <div className="max-w-md">
          <input type="range" min={0} max={100} step={5} value={pct}
            onChange={(e) => set({ min_confidence: (parseInt(e.target.value, 10) || 0) / 100 })}
            className="w-full accent-brand" aria-label="How sure the AI must be" />
          <div className="flex justify-between text-[11px] text-muted-foreground"><span>More alerts</span><span className="font-medium text-foreground">{pct}%</span><span>Fewer, surer alerts</span></div>
          <p className="mt-1 text-[12px] text-foreground">{sureText(pct)}</p>
        </div>
      </Step>

      <Step n={5} title="How serious is it?">
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {SEVERITIES.map((s) => (
            <button key={s.value} type="button" onClick={() => set({ severity: s.value })} aria-pressed={severity === s.value}
              className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left ${severity === s.value ? 'border-brand bg-brand-light' : 'border-border hover:bg-muted'}`}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEV_DOT[s.value]}`} />
              <span>
                <span className="block text-[13px] font-medium text-foreground">{s.label}</span>
                <span className="block text-[11px] text-muted-foreground">{s.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </Step>

      <Step n={6} title="Don't repeat it for…" hint="After an alert, the same event on the same camera is not alerted again until this time has passed.">
        <select value={COOLDOWNS.some((c) => c.value === cooldown) ? cooldown : 'custom'} onChange={(e) => set({ cooldown_seconds: Number(e.target.value) })} className={`${aaInputCls} max-w-xs`}>
          {COOLDOWNS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          {!COOLDOWNS.some((c) => c.value === cooldown) && <option value="custom" disabled>{cooldown} seconds</option>}
        </select>
      </Step>

      <Step n={7} title="Where should it go?" hint="On the alert board for operators, and / or to people by email or WhatsApp (Routing).">
        <label className="flex items-center gap-2 text-[13px] text-foreground">
          <input type="checkbox" checked={isDashboardOn(form)} onChange={(e) => set({ websocket_enabled: e.target.checked })} className="h-4 w-4 accent-brand" />
          <span><b>Alert board</b> — operators see it live</span>
        </label>
        {routing ? (
          <div className="max-w-2xl rounded-lg border border-border">
            <p className="border-b border-border px-3 py-2 text-[12px] text-muted-foreground">Send to these notifications (Routing) — tick all that should tell people about this rule</p>
            {notes === null && <p className="px-3 py-2 text-[12px] text-muted-foreground">Loading…</p>}
            {notes?.error && <p className="px-3 py-2 text-[12px] text-destructive">{notes.error}</p>}
            {notes && !notes.error && custom.length === 0 && <p className="px-3 py-2 text-[12px] text-muted-foreground">No notifications yet — create one in Routing.</p>}
            {custom.map((p) => {
              const on = picked.includes(p.id);
              const wasOnly = !on && notificationsOfRule([p], routing.ruleId).length > 0 && onlyRuleOf(p, routing.ruleId);
              return (
                <label key={p.id} className="flex cursor-pointer items-start gap-2 border-b border-border px-3 py-2 last:border-0 hover:bg-muted">
                  <input type="checkbox" checked={on} onChange={() => togglePick(p.id)} className="mt-0.5 h-4 w-4 accent-brand" />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-foreground">{p.name}{!p.enabled && <span className="font-normal text-muted-foreground"> (off)</span>}</span>
                    <span className="block text-[12px] text-muted-foreground">Tells {whoSummary(p, notes?.dir || null)}</span>
                    {wasOnly && <span className="block text-[12px] text-warning">This is its only rule — it will be turned off.</span>}
                  </span>
                </label>
              );
            })}
          </div>
        ) : null}
        {routing && notes && !picked.length && (
          <p className="text-[12px] text-muted-foreground">
            None ticked — this rule's alerts go to <b>Everything else</b>{fallback ? `, which tells ${whoSummary(fallback, notes.dir)}` : ''}.
          </p>
        )}
        <p className="text-[12px] text-muted-foreground">
          {routingHref ? <a href={routingHref} className="font-medium text-brand hover:underline">Open Routing</a> : 'Routing'} to add people or create a new notification.
        </p>
      </Step>

      <Step n={8} title="Alert message" hint="What the alert says on the board, in emails and on WhatsApp. Click a variable to put it where the cursor is.">
        <textarea
          ref={msgRef}
          rows={3}
          maxLength={500}
          value={form.message_template || ''}
          onChange={(e) => set({ message_template: e.target.value })}
          placeholder={suggested}
          className={`${aaInputCls} max-w-2xl resize-y`}
        />
        <div className="flex max-w-2xl flex-wrap items-center gap-1.5">
          {form.message_template !== suggested && (
            <button type="button" onClick={() => set({ message_template: suggested })} className="rounded-lg border border-dashed border-brand px-2.5 py-1 text-[12px] font-medium text-brand hover:bg-brand-light">
              Use the suggested message{family ? ` for ${family.label}` : ''}
            </button>
          )}
          {MESSAGE_VARIABLES.map((v) => (
            <button key={v.name} type="button" onClick={() => insertVar(v.name)} title={`${v.label}, e.g. ${v.sample}`}
              className="rounded-full border border-border px-2 py-0.5 font-mono text-[11px] text-foreground hover:bg-muted">
              {`\${${v.name}}`}
            </button>
          ))}
          {eventFields.map((f) => (
            <button key={f} type="button" onClick={() => insertVar(f)} title="Sent by the AI with this event"
              className="rounded-full border border-dashed border-border px-2 py-0.5 font-mono text-[11px] text-muted-foreground hover:bg-muted">
              {`\${${f}}`}
            </button>
          ))}
        </div>
        <div className="max-w-2xl rounded-lg border border-border px-3 py-2">
          <p className={aaLabelCls}>How it will read</p>
          <p className="text-[13px] text-foreground">{previewMessage(form.message_template || suggested, { alert_name: form.name || subChoice?.label || family?.label || 'Alert' })}</p>
        </div>
      </Step>

      <section className="rounded-xl border border-border">
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <h4 className="min-w-0 flex-1 text-[14px] font-semibold text-foreground">The rule, exactly as it will be saved</h4>
          <button type="button" onClick={() => setEditExact((v) => !v)}
            className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-[12px] text-foreground hover:bg-muted">
            {editExact ? <><CheckIcon className="h-4 w-4" /> Done</> : <><PencilSquareIcon className="h-4 w-4" /> Edit</>}
          </button>
        </div>
        <dl className="px-4 py-2">
          <Row k="Name">{form.name || <span className="text-muted-foreground">(none — the event's name is used)</span>}</Row>
          <Row k="Event names"><span className="font-mono text-[12px]">{names.length ? names.join(', ') : '* (any)'}</span></Row>
          <Row k="Cameras">{scope === 'all' ? 'All' : scope === 'group' ? `Group: ${groupName || '—'}` : scope === 'camera' ? camName(form.camera_id) : listed.map(camName).join(', ') || '—'}</Row>
          <Row k="AI at least">{pct}% sure</Row>
          <Row k="Severity">{SEVERITIES.find((s) => s.value === severity)?.label}</Row>
          <Row k="Repeat">{COOLDOWNS.find((c) => c.value === cooldown)?.label || `every ${cooldown} s`}</Row>
          <Row k="Alert board">{isDashboardOn(form) ? 'Shown' : 'Not shown'}</Row>
          {routing && notes && <Row k="Notifications">{picked.length ? custom.filter((p) => picked.includes(p.id)).map((p) => p.name).join(', ') : 'None — Everything else'}</Row>}
          <Row k="Message"><span className="font-mono text-[12px]">{form.message_template || '(the event\'s own text)'}</span></Row>
          <Row k="Extra conditions">{conditions || 'None'}</Row>
          {form.description && <Row k="Fallback text">{form.description}</Row>}
          <Row k="Webhook">{form.webhook_enabled ? <span className="font-mono text-[12px]">{form.webhook_url || '(no address yet)'}</span> : 'Off'}</Row>
        </dl>
        {editExact && (
          <div className="space-y-4 border-t border-border px-4 py-3">
            <div>
              <p className="text-[13px] font-medium text-foreground">Extra conditions</p>
              <p className="mb-1 text-[12px] text-muted-foreground">Only alert when a value the AI sends passes a test (e.g. temperature over 60), or after it has held for a while.</p>
              <RuleConditionsFields value={form} onChange={(patch) => set(patch)} catalog={catalog || []} hideMessage />
            </div>
            <label className="block max-w-2xl">
              <span className={aaLabelCls}>Fallback text (used when there is no message above and the AI sends none)</span>
              <input value={form.description || ''} onChange={(e) => set({ description: e.target.value })} className={aaInputCls} />
            </label>
            <div>
              <label className="flex items-center gap-2 text-[13px] text-foreground">
                <input type="checkbox" checked={!!form.webhook_enabled} onChange={(e) => set({ webhook_enabled: e.target.checked })} className="accent-brand" />
                <LinkIcon className="h-4 w-4 text-muted-foreground" /> Also send it to another system (webhook)
              </label>
              {form.webhook_enabled && (
                <input value={form.webhook_url || ''} onChange={(e) => set({ webhook_url: e.target.value })} className={`${aaInputCls} mt-1.5 max-w-2xl`} placeholder="https://…" />
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
